import { Injectable } from '@angular/core';
import { ProductAnalyticsService, ProductAnalyticsEvent } from './product-analytics.service';
import { IOrderInfo, ITaskInfo, IVehicle, IVehicleAddress } from 'src/app/interfaces/task-info';
import { SelectItem } from 'primeng';
import { TaskTypeHelper } from 'src/app/utils/taskType.helper';
import { timeToHoursMinutesSeconds } from 'src/app/utils/timeToHoursMinutes';

export enum AnalyticsOrderEvent {
  create_order_vehicle_step_continue = 0,
  create_order_route_timings_continue = 1,
  create_order_review_continue = 2,
  order_price_summary_view = 3,
  new_task_order = 4,
  edit_task_order = 5,
  order_price_summary_exit = 6,
  order_list_summary_view = 7,
  order_price_toast_success = 8,
}

interface AnalyticsOrder {
  vehicleRegistration: string;
  vehicleModel: string;
  vehicleKerbWeight: string;
  movementType: string;
  collectionAddress: string;
  deliveryAddress: string;
  taskDistance: string;
  estimatedTime: string;
  earliestCollectionDate: string;
  latestCollectionDate: string;
  priceMin: string;
  priceMax: string;
}

export interface AnalyticsCreateOrderComponent {
  triggerEvent: AnalyticsOrderEvent;
  vehicle: IVehicle;
  isFollowOnJourney: boolean;
  isReturnJourney: boolean;
  newJobs: Array<IOrderInfo>;
  task: ITaskInfo;
  pickupLocation: IVehicleAddress;
  dropoffLocation: IVehicleAddress;
  taskCount: number;
  taskType: number;
  specificStepGroups: SelectItem[];
  selectedGroupId: number;
  selectedOrder: number;
}

@Injectable({ providedIn: 'root' })
export class CreateOrderAnalyticsService {
  constructor(
    private productAnalyticsService: ProductAnalyticsService,
    private taskTypeHelper: TaskTypeHelper,
  ) {}

  sendEvent(order: AnalyticsCreateOrderComponent) {
    if (order.triggerEvent === AnalyticsOrderEvent.create_order_vehicle_step_continue) {
      void this.productAnalyticsService.trackEvent(
        ProductAnalyticsEvent.CreateOrderVehicleStepContinue,
        {
          pageName: 'Vehicle',
          pageTitle: 'Create Order - Vehicle',
          isFirstStep: true,
          journeyType: order.isFollowOnJourney
            ? 'Follow on journey'
            : order.isReturnJourney
            ? 'Return Journey'
            : '',
          vehicleRegistration: order.vehicle.registrationNumber,
          vinNumber: order.vehicle.vin,
          vehicleKerbWeight: order.vehicle.kerbWeight,
          fuelType: order.vehicle.fuelType,
          customerDepartment: order.task.furtherInformation.customerDepartmentId,
          customerTeam: order.task.furtherInformation.customerTeamId,
          taskTypeId: order.task.taskTypeId,
        },
      );
    } else if (order.triggerEvent === AnalyticsOrderEvent.create_order_route_timings_continue) {
      void this.productAnalyticsService.trackEvent(
        ProductAnalyticsEvent.CreateOrderRouteTimingsContinue,
        {
          pageName: 'Route and Timings',
          pageTitle: 'Create Order - Route and Timings',
          userType: 'customer',
          journeyType: order.isFollowOnJourney
            ? 'Follow on journey'
            : order.isReturnJourney
            ? 'Return Journey'
            : '',
          taskCount: order.taskCount,
        },
      );
    } else if (order.triggerEvent === AnalyticsOrderEvent.create_order_review_continue) {
      const estimateTimeInMinutes = Math.round(
        timeToHoursMinutesSeconds(order.task.estimateTime, 'minutes'),
      );
      void this.productAnalyticsService.trackEvent(
        ProductAnalyticsEvent.CreateOrderReviewContinue,
        {
          pageName: 'Review',
          pageTitle: 'Create Order - Review',
          vehicleRegistration: order.vehicle.registrationNumber,
          vinNumber: order.vehicle.vin,
          vehicleMake: order.vehicle.make,
          vehicleModel: order.vehicle.model,
          vehicleKerbWeight: order.vehicle.kerbWeight,
          taskDistance: order.task.distance,
          estimatedTime: estimateTimeInMinutes,
          movementType: this.taskTypeHelper.getTaskTypeName(order.taskType),
          customerDepartment: order.task.furtherInformation.customerDepartmentId,
          customerTeam: order.task.furtherInformation.customerTeamId,
          taskCount: order.taskCount,
          journeyType: order.isFollowOnJourney
            ? 'Follow on journey'
            : order.isReturnJourney
            ? 'Return Journey'
            : '',
        },
      );
    } else if (order.triggerEvent === AnalyticsOrderEvent.order_price_summary_view) {
      const orders = this.getAnalyticsOrder(order.newJobs);
      void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.OrderPriceSummaryView, {
        pageName: 'Review',
        pageTitle: 'Create Order - Review',
        orderCount: orders.length,
        orders: orders,
      });
    } else if (order.triggerEvent === AnalyticsOrderEvent.new_task_order) {
      const orders = this.getAnalyticsOrder(order.newJobs);
      void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.NewTaskOrder, {
        pageName: 'Order List',
        pageTitle: 'Order List - Order 2',
        orderCount: orders.length,
        orders: orders,
      });
    } else if (order.triggerEvent === AnalyticsOrderEvent.edit_task_order) {
      const orders = this.getAnalyticsOrder(order.newJobs);
      void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.EditTaskOrder, {
        pageName: 'Order List',
        pageTitle: 'Order List - Order 2',
        taskCount: order.taskCount,
        orders: orders,
      });
    } else if (order.triggerEvent === AnalyticsOrderEvent.order_price_summary_exit) {
      const orders = this.getAnalyticsOrder(order.newJobs);
      void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.OrderPriceSummaryExit, {
        pageName: 'Create Order',
        pageTitle: 'Create Order',
        orderCount: orders.length,
        orders: orders,
      });
    } else if (order.triggerEvent === AnalyticsOrderEvent.order_list_summary_view) {
      const orders = this.getAnalyticsOrder(order.newJobs);
      void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.OrderListSummaryView, {
        pageName: 'Order Summary',
        pageTitle: 'Create Order - Review',
        orderElementInViewCount: order.newJobs.length,
        orderCount: order.newJobs.length,
        orderElementInView: order.selectedOrder,
        orders: orders,
      });
    } else if (order.triggerEvent === AnalyticsOrderEvent.order_price_toast_success) {
      const orders = this.getAnalyticsOrder(order.newJobs);
      void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.OrderPriceToastSuccess, {
        pageName: 'Create Order',
        pageTitle: 'Create Order',
        taskCount: order.taskCount,
        orders: orders,
      });
    }
  }
  private getAnalyticsOrder(newJobs: Array<IOrderInfo>): AnalyticsOrder[] {
    const orders: AnalyticsOrder[] = [];

    newJobs.forEach((job) => {
      job.tasks.forEach((task) => {
        const estimateTimeInMinutes = Math.round(
          timeToHoursMinutesSeconds(task.estimateTime, 'minutes'),
        );
        const order: AnalyticsOrder = {
          collectionAddress: task?.pickupLocation?.primaryAddress ?? '',
          deliveryAddress: task?.dropoffLocation?.primaryAddress ?? '',
          earliestCollectionDate: task?.pickupEarliestDateTime ?? '',
          estimatedTime: estimateTimeInMinutes.toString() ?? '',
          latestCollectionDate: task?.dropoffLatestDateTime ?? '',
          movementType: task?.taskTypeId?.toString() ?? '',
          priceMax: job?.customerPriceMax?.toString() ?? '',
          priceMin: job?.customerPriceMin?.toString() ?? '',
          taskDistance: task?.distance?.toString() ?? '',
          vehicleKerbWeight: task?.vehicle?.kerbWeight?.toString() ?? '',
          vehicleModel: task?.vehicle?.model ?? '',
          vehicleRegistration: task?.vehicle?.registrationNumber ?? '',
        };

        orders.push(order);
      });
    });

    return orders;
  }
}
